import { ReviewsPageContextBindings } from '@/context/reviews-page.contract';
import { ReviewsPageState } from '@/context/reviews-page.state';
import { useReviewsPageActions } from '@/context/use-reviews-page-actions';
import { useReviewsPageProviderConnections } from '@/context/use-reviews-page-provider-connections';

type ReviewsPageActions = Pick<
    ReturnType<typeof useReviewsPageActions>,
    | 'setPickedReviews'
    | 'setPickedProducts'
    | 'setPickedBrandGroups'
    | 'setSort'
    | 'setFilters'
    | 'setAiAnalysisProgress'
    | 'setExportProgress'
    | 'setAutoReplyProgress'
>;

type ReviewsPageConnections = ReturnType<typeof useReviewsPageProviderConnections>;

type BuildReviewsPageProviderBindingsArgs = {
    state: ReviewsPageState;
    actions: ReviewsPageActions;
    connections: ReviewsPageConnections;
    maxSelectedItems: number | null;
};

export const buildReviewsPageProviderBindings = ({
    state,
    actions,
    connections,
    maxSelectedItems,
}: BuildReviewsPageProviderBindingsArgs): ReviewsPageContextBindings => ({
    selection: {
        selectionSessionId: state.selectionSessionId,
        pickedReviews: state.pickedReviews,
        pickedProducts: state.pickedProducts,
        pickedBrandGroups: state.pickedBrandGroups,
        setPickedReviews: actions.setPickedReviews,
        setPickedProducts: actions.setPickedProducts,
        setPickedBrandGroups: actions.setPickedBrandGroups,
    },
    data: {
        reviews: state.reviews,
        products: state.products,
        brandGroups: state.brandGroups,
        total: state.total,
    },
    query: {
        sort: state.sort,
        setSort: actions.setSort,
        refetchReviewsAndProducts: connections.refetchReviewsAndProducts,
        filters: state.filters,
        filtersQuery: state.filtersQuery,
        setFilters: actions.setFilters,
        reviewsOnPage: state.reviewsOnPage,
        setReviewsOnPage: connections.setReviewsOnPage,
        maxSelectedItems,
    },
    transport: {
        enqueueReviewSelectionMutation: connections.enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile: connections.waitForReviewSelectionReconcile,
        getProducts: connections.getProducts,
        getBrandGroups: connections.getBrandGroups,
        refetchReviews: connections.refetchReviews,
        isReviewsDataReconciliationRunning: connections.isReviewsDataReconciliationRunning,
        isReviewsDataReconciliationSelectionVisualRunning:
            connections.isReviewsDataReconciliationSelectionVisualRunning,
    },
    progress: {
        aiAnalysisProgress: state.aiAnalysisProgress,
        setAiAnalysisProgress: actions.setAiAnalysisProgress,
        exportProgress: state.exportProgress,
        setExportProgress: actions.setExportProgress,
        autoReplyProgress: state.autoReplyProgress,
        setAutoReplyProgress: actions.setAutoReplyProgress,
    },
});
